import { Account, AccountKind, BalanceIndex, Txn } from '../money.model';
import { spendableTxns } from '../dashboard/dashboard.aggregate';
import { addDays } from '../rules/recurring.detector';

export interface AccountSummary {
  id: string;
  name: string;
  kind: AccountKind;
  mask: string;
  /** Latest recorded balance in cents, or null when the source reports none (a card export does not). */
  balanceCents: number | null;
  /** ISO date that balance was recorded, or '' when there is none. */
  balanceDate: string;
  txnCount: number;
  firstDate: string;
  lastDate: string;
  /** Real outflow only — transfers and excluded rows removed — over the trailing window. */
  windowSpendCents: number;
}

export interface BalanceMonth {
  month: string;
  /** The date the figure was actually recorded; earlier than `month` when carried forward. */
  date: string;
  cents: number;
}

/** Two years, matching the import window's DEFAULT_CUTOFF_MONTHS. */
export const BALANCE_MONTHS = 24;
export const SPEND_WINDOW_DAYS = 30;

/** The newest recorded balance for one account, or null when it has no history. */
export function latestBalance(
  points: Record<string, number> | undefined,
): { date: string; cents: number } | null {
  const dates = Object.keys(points ?? {}).sort();
  if (!dates.length) return null;
  const date = dates[dates.length - 1];
  return { date, cents: (points as Record<string, number>)[date] };
}

export function nextMonth(month: string): string {
  const year = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  return m === 12 ? `${year + 1}-01` : `${year}-${String(m + 1).padStart(2, '0')}`;
}

/**
 * One closing balance per calendar month, contiguous, newest `limit` months.
 *
 * Contiguity matters because the line chart spaces its points evenly by index:
 * a missing month would silently compress the time axis and draw a slope that
 * never happened. A month with no recorded point carries the previous close
 * forward, which is what actually happened — a balance is a stock, and with no
 * transaction to move it, it did not move.
 *
 * Month granularity rather than every recorded point, for two reasons: the raw
 * series is 325 irregularly-spaced points, which an index-spaced axis would
 * misrepresent, and each point renders as a focusable control, which would put
 * 325 tab stops in one chart.
 */
export function monthEndBalances(
  points: Record<string, number> | undefined, limit: number,
): BalanceMonth[] {
  const entries = Object.entries(points ?? {}).sort(([a], [b]) => a.localeCompare(b));
  if (!entries.length) return [];

  const closes = new Map<string, BalanceMonth>();
  entries.forEach(([date, cents]) => {
    closes.set(date.slice(0, 7), { month: date.slice(0, 7), date, cents });
  });

  const lastMonth = entries[entries.length - 1][0].slice(0, 7);
  const filled: BalanceMonth[] = [];
  let month = entries[0][0].slice(0, 7);

  // Only the last `limit` months are ever returned; this cap just bounds the
  // walk if a malformed (e.g. mistyped-year) key ever made the range huge —
  // it should never bind for well-formed data.
  const cap = limit * 12;
  while (filled.length < cap) {
    const point = closes.get(month);
    const carried = point ?? { ...filled[filled.length - 1], month };
    filled.push(carried);
    if (month === lastMonth) break;
    month = nextMonth(month);
  }

  return filled.slice(-limit);
}

/**
 * One row per account. Accounts whose balance is known lead, because that is
 * the figure the screen exists to show; a card export carries no balance at
 * all, so those rows sort after the rest (alphabetically by name within each
 * group) instead of by balance. The card itself renders the same layout
 * either way — the balance line just shows "—".
 */
export function accountSummaries(
  accounts: Account[], txns: Txn[], balances: BalanceIndex,
  asOf: string, windowDays = SPEND_WINDOW_DAYS,
): AccountSummary[] {
  const spendable = spendableTxns(txns ?? []);
  const since = addDays(asOf, -windowDays);

  return (accounts ?? []).map((a): AccountSummary => {
    const mine = (txns ?? []).filter(t => t.accountId === a.id);
    const dates = mine.map(t => t.date).sort();
    const latest = latestBalance((balances ?? {})[a.id]);

    return {
      id: a.id, name: a.name, kind: a.kind, mask: a.mask,
      balanceCents: latest ? latest.cents : null,
      balanceDate: latest ? latest.date : '',
      txnCount: mine.length,
      firstDate: dates[0] ?? '',
      lastDate: dates[dates.length - 1] ?? '',
      windowSpendCents: spendable
        .filter(t => t.accountId === a.id && t.amount < 0
          && t.date > since && t.date <= asOf)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0),
    };
  }).sort((a, b) => {
    const known = Number(b.balanceCents !== null) - Number(a.balanceCents !== null);
    return known !== 0 ? known : a.name.localeCompare(b.name);
  });
}
